// File: Components/InvoicePrint.jsx
import React, { forwardRef } from "react";

const InvoicePrint = forwardRef(({ data }, ref) => {
    const customers = data?.customer_details || [];
    const roomRentDetails = data?.room_rent_details || [];
    const billing = data?.billing_details?.[0] || {};
    const settlement = data?.settlement || [];

    // Extracted values from data (passed from parent state)
    const room_rent_amount = Number(data?.totals?.room_rent_amount || 0);
    const tax = Number(data?.totals?.tax || 0);
    const service_charge_amount = Number(billing?.service_charge_amount || 0);
    const advance_amount = Number(billing?.advance_amount || 0);
    const extra_services_amount = Number(billing?.extra_services_amount || 0);
    const complementary_amount = Number(billing?.complementary_amount || 0);

    // New fields from state
    const offer_discount_amount = Number(data?.totals?.discountAmount || 0);
    const additional_charge = Number(data?.totals?.additionalChargeAmount || 0);
    const credit_amount = Number(data?.totals?.creditAmount || 0);
    const special_discount_amount = Number(data?.totals?.specialDiscountAmount || 0);
    const final_payable = Number(data?.totals?.finalPayable || 0);
    const collected = Number(data?.totals?.collected || 0);

    return (
        <div
            ref={ref}
            style={{
                padding: "40px",
                fontSize: "12px",
                fontFamily: "Arial, sans-serif",
                color: "#333",
                backgroundColor: "white", // Force white background
                width: "100%",
                minHeight: "100%"
            }}
        >
            <style>
                {`
  @media print {
                    @page { size: A4; margin: 10mm; }
                    body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                }
                /* Ensure tables don't break awkwardly */
                .invoice-table { width: 100%; border-collapse: collapse; page-break-inside: auto; }
                .invoice-table tr { page-break-inside: avoid; page-break-after: auto; }
                    .text-right { text-align: right; }
                    .text-center { text-align: center; }
                    .summary-container { display: flex; justify-content: flex-end; margin-top: 20px; }
                    .summary-table { width: 45%; border-collapse: collapse; }
                    .summary-table td { padding: 5px; border-bottom: 1px solid #eee; }
                    .summary-table .label { font-weight: bold; text-align: left; }
                    .summary-table .value { text-align: right; }
                    .total-row { background-color: #f8f9fa; font-weight: bold; font-size: 14px; }
                    .header { background: #333; color: #fff; padding: 20px; text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; }
                    .section-title { font-size: 12px; font-weight: bold; margin-top: 20px; margin-bottom: 10px; background: #333; color: #fff; padding: 5px 10px; }
                `}
            </style>

            {/* CUSTOMER DETAILS */}
            <div className="section-title">GUEST INFORMATION</div>
            <table style={{ width: "100%", marginBottom: "20px" }}>
                <tbody>
                    <tr>
                        <td style={{ width: "15%", fontWeight: "bold" }}>Guest Name:</td>
                        <td style={{ width: "35%" }}>{customers[0]?.name || "N/A"}</td>
                        <td style={{ width: "15%", fontWeight: "bold" }}>Booking No:</td>
                        <td style={{ width: "35%" }}>{customers[0]?.booking_no || "N/A"}</td>
                    </tr>
                    <tr>
                        <td style={{ fontWeight: "bold" }}>Room(S):</td>
                        <td>{customers.length}</td>
                        <td style={{ fontWeight: "bold" }}>Mobile:</td>
                        <td>{customers[0]?.mobile_no || "N/A"}</td>
                    </tr>
                    <tr>
                        <td style={{ fontWeight: "bold" }}>Check‑In:</td>
                        <td>{roomRentDetails[0]?.check_in_date || "N/A"}</td>
                        <td style={{ fontWeight: "bold" }}>Check‑Out:</td>
                        <td>{roomRentDetails[0]?.check_out_date || "N/A"}</td>
                    </tr>
                </tbody>
            </table>

            {/* ROOM RENT DETAILS */}
            <div className="section-title">ROOM RENT DETAILS</div>
            <table className="invoice-table">
                <thead>
                    <tr>
                        <th className="text-left">Room No</th>
                        <th className="text-center">Days</th>
                        <th className="text-right">Rate</th>
                        <th className="text-right">Tax</th>
                        <th className="text-right">Total</th>
                    </tr>
                </thead>
                <tbody>
                    {roomRentDetails.map((r, i) => (
                        <tr key={i}>
                            <td>{r.room_no} ({r.room_type})</td>
                            <td className="text-center">{r.no_of_days}</td>
                            <td className="text-right">₹{Number(r.rent_per_day).toFixed(2)}</td>
                            <td className="text-right">₹{Number(r.tax).toFixed(2)}</td>
                            <td className="text-right">₹{Number(r.grand_total).toFixed(2)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* POSTED BILLS */}
            {data?.posted_bills?.length > 0 && (
                <>
                    <div className="section-title">POSTED SERVICES (Parking, Pool, Transport)</div>
                    <table className="invoice-table">
                        <thead>
                            <tr>
                                <th className="text-left">Service Type</th>
                                <th className="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.posted_bills.map((b, i) => (
                                <tr key={i}>
                                    <td>{b.bill_type}</td>
                                    <td className="text-right">₹{Number(b.total).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {/* BILLING SUMMARY */}
            <div className="summary-container">
                <table className="summary-table">
                    <tbody>
                        <tr>
                            <td className="label">Room Rent Total:</td>
                            <td className="value">₹{room_rent_amount.toFixed(2)}</td>
                        </tr>
                        {offer_discount_amount > 0 && (
                            <tr>
                                <td className="label">Offer Discount ({billing.offer_discount}%):</td>
                                <td className="value">- ₹{offer_discount_amount.toFixed(2)}</td>
                            </tr>
                        )}
                        <tr>
                            <td className="label">Service Charge:</td>
                            <td className="value">₹{service_charge_amount.toFixed(2)}</td>
                        </tr>
                        <tr>
                            <td className="label">Tax (GST):</td>
                            <td className="value">₹{tax.toFixed(2)}</td>
                        </tr>
                        {extra_services_amount > 0 && (
                            <tr>
                                <td className="label">Extra Bed/Services:</td>
                                <td className="value">₹{extra_services_amount.toFixed(2)}</td>
                            </tr>
                        )}
                        {additional_charge > 0 && (
                            <tr>
                                <td className="label">Additional Charges:</td>
                                <td className="value">₹{additional_charge.toFixed(2)}</td>
                            </tr>
                        )}
                        {advance_amount > 0 && (
                            <tr>
                                <td className="label">Advance Paid:</td>
                                <td className="value">- ₹{advance_amount.toFixed(2)}</td>
                            </tr>
                        )}
                        {credit_amount > 0 && (
                            <tr>
                                <td className="label">Credit / Adjustment:</td>
                                <td className="value">- ₹{credit_amount.toFixed(2)}</td>
                            </tr>
                        )}
                        {special_discount_amount > 0 && (
                            <tr>
                                <td className="label">Special Discount:</td>
                                <td className="value">- ₹{special_discount_amount.toFixed(2)}</td>
                            </tr>
                        )}
                        <tr className="total-row">
                            <td className="label">NET PAYABLE:</td>
                            <td className="value">₹{final_payable.toFixed(2)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* PAYMENT DETAILS */}
            <div className="section-title">PAYMENT SETTLEMENT</div>
            <table className="invoice-table">
                <thead>
                    <tr>
                        <th>Payment Mode</th>
                        <th className="text-right">Amount Received</th>
                    </tr>
                </thead>
                <tbody>
                    {settlement.filter(s => s.amount > 0).map((pay, i) => (
                        <tr key={i}>
                            <td>{pay.payment_mode_name}</td>
                            <td className="text-right">₹{Number(pay.amount).toFixed(2)}</td>
                        </tr>
                    ))}
                    <tr style={{ fontWeight: "bold" }}>
                        <td className="text-right">Total Collected:</td>
                        <td className="text-right">₹{collected.toFixed(2)}</td>
                    </tr>
                </tbody>
            </table>

            <div style={{ marginTop: "50px", display: "flex", justifyContent: "space-between" }}>
                <div style={{ textAlign: "center" }}>
                    <p style={{ marginBottom: "40px" }}>_______________________</p>
                    <p>Guest Signature</p>
                </div>
                <div style={{ textAlign: "center" }}>
                    <p style={{ marginBottom: "40px" }}>_______________________</p>
                    <p>Authorized Signatory</p>
                </div>
            </div>
        </div>
    );
});

InvoicePrint.displayName = "InvoicePrint";
export default InvoicePrint;
